import React, { useEffect, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { PlayCircleIcon, ShieldIcon } from 'lucide-react';
import { Container } from './ui/Container';
import { Button } from './ui/Button';
import { SectionEyebrow } from './ui/SectionEyebrow';
// Animated Network Background Component
const NetworkBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    let nodes: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
    }[] = [];
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes();
    };
    const initNodes = () => {
      nodes = [];
      const numNodes = Math.floor(window.innerWidth * window.innerHeight / 25000);
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 1.5 + 0.5
        });
      }
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(22, 163, 74, ${0.15 - dist / 1000})`;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(34, 197, 94, 0.4)';
        ctx.fill();
      });
      animationFrameId = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-60 pointer-events-none" data-id="element-108" />;
};
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden bg-topo-pattern" data-id="element-109">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-iris-green/10 rounded-full blur-[120px] pointer-events-none" data-id="element-110"></div>

      <NetworkBackground data-id="element-111" />

      <Container className="relative z-10 flex flex-col items-center text-center" data-id="element-112">
        <SectionEyebrow text="Request & Workflow Management" className="mb-6" data-id="element-113" />

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-5xl sm:text-6xl md:text-8xl font-display font-bold text-white leading-[0.9] tracking-tight mb-8 max-w-5xl text-balance" data-id="element-114">
          REQUESTS.
          <br data-id="element-115" />
          <span className="text-iris-green" data-id="element-116">STREAMLINED. SECURE. SIMPLE.</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-lg md:text-xl text-iris-muted max-w-2xl mb-10 text-balance" data-id="element-117">
          Replace spreadsheets, SharePoint, and disconnected systems with one
          structured, trackable workflow platform built for modern agency
          operations.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="flex flex-col sm:flex-row items-center gap-4 mb-20" data-id="element-118">
          <Button size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
          behavior: 'smooth'
        })} data-id="element-119">
            Schedule a Walkthrough
          </Button>
          <Button variant="secondary" size="lg" className="gap-2" data-id="element-120">
            <PlayCircleIcon className="w-5 h-5" data-id="element-121" />
            Watch Overview
          </Button>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="w-full max-w-4xl border-t border-iris-border pt-8" data-id="element-122">
          <p className="text-xs font-mono text-iris-muted uppercase tracking-widest mb-6" data-id="element-123">
            Trusted by agencies across federal, state, and local jurisdictions
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale" data-id="element-124">
            {['STATE FUSION CENTER', 'COUNTY SHERIFF', 'FEDERAL TASK FORCE', 'MUNICIPAL PD'].map((label, i) => <div key={i} className="flex items-center gap-2" data-id="element-125">
                <ShieldIcon className="w-6 h-6 text-iris-muted" data-id="element-126" />
                <span className="font-display text-lg tracking-wider text-iris-muted font-semibold" data-id="element-127">
                  {label}
                </span>
              </div>)}
          </div>
        </motion.div>
      </Container>
    </section>;
}